import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import SignaturePad from "react-signature-canvas";

/**
 * ResponsiveSignaturePad — wrapper around react-signature-canvas that solves
 * the mobile rendering bug.
 *
 * Problem: by default the inner <canvas> drawing surface stays at 300x150
 * regardless of CSS sizing, so touch coordinates map to wrong pixels on phones
 * and `isEmpty()` may report true even when the user has signed.
 *
 * Fix: measure the container with ResizeObserver and pass explicit
 * width/height attributes to the canvas (matching its CSS size). When the
 * canvas is resized we restore the previously drawn data points so the user
 * doesn't lose their signature.
 *
 * Public ref API (compatible with previous usage):
 *   isEmpty(), clear(), getCanvas(), toDataURL(type), fromData(data), toData()
 */
const ResponsiveSignaturePad = forwardRef(function ResponsiveSignaturePad(
    {
        className = "",
        height = 176, // ~h-44
        backgroundColor = "#ffffff",
        penColor = "#000000",
        canvasProps = {},
    },
    ref,
) {
    const containerRef = useRef(null);
    const padRef = useRef(null);
    const [size, setSize] = useState({ w: 0, h: 0 });
    // Preserve drawing across resizes (e.g. on-screen keyboard opening on mobile)
    const lastDataRef = useRef(null);

    useEffect(() => {
        if (!containerRef.current) return undefined;
        const measure = () => {
            const el = containerRef.current;
            if (!el) return;
            const rect = el.getBoundingClientRect();
            const w = Math.max(1, Math.floor(rect.width));
            const h = Math.max(1, Math.floor(rect.height));
            setSize((prev) => (prev.w !== w || prev.h !== h ? { w, h } : prev));
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(containerRef.current);
        // Also re-measure on orientation/viewport changes (iOS quirks)
        window.addEventListener("orientationchange", measure);
        window.addEventListener("resize", measure);
        return () => {
            ro.disconnect();
            window.removeEventListener("orientationchange", measure);
            window.removeEventListener("resize", measure);
        };
    }, []);

    // Re-render existing signature data when the canvas is resized
    useEffect(() => {
        if (!padRef.current) return;
        // Capture current strokes BEFORE the resize wipes them
        if (lastDataRef.current && lastDataRef.current.length > 0) {
            try {
                padRef.current.fromData(lastDataRef.current);
            } catch {
                /* ignore */
            }
        }
    }, [size.w, size.h]);

    // Expose the same API as the underlying lib so the rest of the app doesn't change
    useImperativeHandle(ref, () => ({
        isEmpty: () => (padRef.current ? padRef.current.isEmpty() : true),
        clear: () => {
            lastDataRef.current = null;
            if (padRef.current) padRef.current.clear();
        },
        getCanvas: () => padRef.current?.getCanvas(),
        getTrimmedCanvas: () => padRef.current?.getTrimmedCanvas?.(),
        toDataURL: (type = "image/png", encoderOptions) =>
            padRef.current?.getCanvas().toDataURL(type, encoderOptions),
        fromData: (data) => padRef.current?.fromData(data),
        toData: () => padRef.current?.toData(),
    }), []);

    const onEnd = () => {
        if (padRef.current) {
            lastDataRef.current = padRef.current.toData();
        }
    };

    return (
        <div
            ref={containerRef}
            className={`w-full ${className}`}
            style={{ height, position: "relative", lineHeight: 0 }}
        >
            {size.w > 0 && size.h > 0 && (
                <SignaturePad
                    ref={padRef}
                    backgroundColor={backgroundColor}
                    penColor={penColor}
                    onEnd={onEnd}
                    canvasProps={{
                        width: size.w,
                        height: size.h,
                        style: { width: `${size.w}px`, height: `${size.h}px`, touchAction: "none", display: "block" },
                        ...canvasProps,
                    }}
                />
            )}
        </div>
    );
});

export default ResponsiveSignaturePad;
